import React, { useEffect, useState, useMemo, useRef } from 'react';
import ItemWrapper from 'components/ItemWrapper';
import ListWrapper from 'components/ListWrapper';
import { Input } from '@rebass/forms';
import { List, arrayMove } from 'react-movable';
import { Box, Button, Flex } from 'rebass';
import { FiMove, FiXCircle, FiChevronLeft, FiChevronRight, FiSearch, FiInfo } from 'react-icons/fi';
import { cloneDeep } from 'lodash';
import TooltipIcon from 'components/TooltipIcon';
import helpers from 'utils/helpers';

// Use pagination for large lists to avoid rendering all items at once
const PAGE_SIZE = 500; // Items per page and threshold for enabling pagination

export const MovableOptions = ({ options, onChange, isMovable, isEditable, children }) => {
    const [definitions, setDefinitions] = useState(options);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const prevLengthRef = useRef(options.length);

    // Detect when items are added and navigate to last page
    useEffect(() => {
        const prevLength = prevLengthRef.current;
        setDefinitions(options);

        // Only navigate if list grew AND we're above pagination threshold
        if (options.length > prevLength && options.length > PAGE_SIZE) {
            const lastPage = Math.ceil(options.length / PAGE_SIZE);
            setCurrentPage(lastPage);
            setSearchQuery(''); // Clear search when adding new item
        }

        // Update ref for next comparison
        prevLengthRef.current = options.length;
    }, [options]);

    useEffect(() => {
        onChange(definitions);
    }, [definitions]);

    const handleChange = (e, index, property) => {
        const defs = cloneDeep(definitions);
        defs[index][property] = e.target.value;
        setDefinitions(defs);
    };

    const handleDelete = index => {
        if (confirm('Are you sure?')) {
            const defs = cloneDeep(definitions);
            defs.splice(index, 1);
            setDefinitions(defs);

            // Adjust page if we deleted the last item on the current page
            const totalPages = Math.ceil(defs.length / PAGE_SIZE);
            if (currentPage > totalPages && totalPages > 0) {
                setCurrentPage(totalPages);
            }
        }
    };

    const handleSearch = query => {
        setSearchQuery(query);
        setCurrentPage(1); // Reset to first page when searching
    };

    // Filter definitions based on search query
    const filteredDefinitions = useMemo(() => {
        if (!searchQuery.trim()) {
            return definitions;
        }
        const re = new RegExp(helpers.escapeRegExp(searchQuery), 'i');
        return definitions.filter(item => re.test(item.label) || re.test(item.value));
    }, [definitions, searchQuery]);

    // Show search/pagination bar if original list is large
    const showSearchBar = definitions.length > PAGE_SIZE;

    // Calculate pagination based on filtered results
    const totalPages = Math.ceil(filteredDefinitions.length / PAGE_SIZE);
    const usePagination = filteredDefinitions.length > PAGE_SIZE;

    // Get items for current page
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const visibleDefinitions = usePagination ? filteredDefinitions.slice(startIndex, endIndex) : filteredDefinitions;

    // Map filtered indices back to original indices for editing
    const getOriginalIndex = filteredIndex => {
        const filteredItem = filteredDefinitions[filteredIndex];
        return definitions.findIndex(item => item === filteredItem);
    };

    // Render a single option row
    const renderOption = (option, index, isDragged = false) => {
        // Calculate the index in the filtered array, then map to original
        const filteredIndex = usePagination ? startIndex + index : index;
        const actualIndex = getOriginalIndex(filteredIndex);

        return (
            <ItemWrapper style={{ color: 'black' }}>
                <Box width={1 / 2} style={{ paddingRight: '16px' }}>
                    <Input
                        value={option.label}
                        onChange={e => handleChange(e, actualIndex, 'label')}
                        disabled={!isEditable}
                        required
                    />
                </Box>
                <Box width={1 / 2}>
                    <Flex>
                        <Input
                            value={option.value}
                            mr={3}
                            onChange={e => handleChange(e, actualIndex, 'value')}
                            disabled={true}
                            required
                        />
                        {isMovable && !usePagination && !searchQuery && (
                            <Button
                                data-movable-handle
                                type="button"
                                variant="secondary-gray"
                                className="secondary-icon"
                                tabIndex={-1}
                                mr={2}
                                style={{
                                    cursor: isDragged ? 'grabbing' : 'grab',
                                    flexShrink: 0
                                }}
                            >
                                <FiMove />
                            </Button>
                        )}
                        {isEditable && (
                            <Button
                                type="button"
                                variant="secondary-gray"
                                className="secondary-icon"
                                onClick={() => handleDelete(actualIndex)}
                                style={{
                                    flexShrink: 0
                                }}
                            >
                                <FiXCircle />
                            </Button>
                        )}
                    </Flex>
                </Box>
            </ItemWrapper>
        );
    };

    return (
        <>
            {showSearchBar && (
                <Flex
                    className="!mb-3"
                    style={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px'
                    }}
                >
                    <Box style={{ position: 'relative', flex: 1 }}>
                        <Box
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#999'
                            }}
                        >
                            <FiSearch />
                        </Box>
                        <Input
                            type="text"
                            placeholder="Search options..."
                            value={searchQuery}
                            onChange={e => handleSearch(e.target.value)}
                            style={{ paddingLeft: '36px', width: '100%' }}
                        />
                    </Box>
                    <Flex style={{ gap: '4px', alignItems: 'center' }}>
                        <Button
                            type="button"
                            variant="secondary-gray"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            style={{ padding: '4px 8px', minWidth: 'auto' }}
                        >
                            <FiChevronLeft style={{ marginRight: 0 }} />
                        </Button>
                        <Box style={{ fontSize: '13px', color: '#666', minWidth: '50px', textAlign: 'center' }}>
                            {currentPage} / {totalPages}
                        </Box>
                        <Button
                            type="button"
                            variant="secondary-gray"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            style={{ padding: '4px 8px', minWidth: 'auto' }}
                        >
                            <FiChevronRight style={{ marginRight: 0 }} />
                        </Button>
                    </Flex>
                </Flex>
            )}
            {definitions.length > PAGE_SIZE && isMovable && (
                <div
                    className="text-secondary-dark fs-12 p-2 rounded mb-3"
                    style={{ backgroundColor: 'rgb(248, 249, 250)' }}
                >
                    <FiInfo className="mr-1" /> Drag-and-drop disabled for large lists. Use alphabetical sorting to
                    organize items.
                </div>
            )}
            <ListWrapper
                style={{
                    position: 'relative',
                    border: '1px solid #eee',
                    borderRadius: '4px'
                }}
            >
                <ItemWrapper style={{ padding: '8px 16px', minHeight: '32px' }}>
                    <Box width={1 / 2}>
                        Label
                        <TooltipIcon for={`tooltip-label`} margin="0px 0 0 4px" />
                    </Box>
                    <Box width={1 / 2}>ID</Box>
                </ItemWrapper>
                <List
                    values={visibleDefinitions}
                    onChange={({ oldIndex, newIndex }) => {
                        // Only allow reordering when viewing the full, unpaginated, unfiltered list
                        // to avoid index mismatch between visibleDefinitions and definitions
                        if (isMovable && !usePagination && !searchQuery.trim()) {
                            const movedOptions = arrayMove(definitions, oldIndex, newIndex);
                            setDefinitions(movedOptions);
                        }
                    }}
                    renderList={({ children, props }) => <div {...props}>{children}</div>}
                    renderItem={({ value, index, props, isDragged }) => {
                        const option = value;
                        return <div {...props}>{renderOption(option, index, isDragged)}</div>;
                    }}
                />
                {children}
            </ListWrapper>
        </>
    );
};
